import React from "react";
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  Show,
  SimpleShowLayout,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  ArrayInput,
  SimpleFormIterator,
  DateField,
  required,
  minValue,
  maxValue,
  SearchInput,
  ReferenceField,
  ReferenceInput,
  AutocompleteInput,
  SelectInput,
  BooleanField,
  BooleanInput,
  SelectField,
  FilterButton,
  CreateButton,
  TopToolbar,
} from "react-admin";
import StringArrayField from "../components/StringArrayField";
import { useWatch } from "react-hook-form";
import { SEVERITY_CHOICES } from "../consts";
import ExportAllButton from "../components/ExportAllButton";
import DynamicDropdownFilter from "../components/DynamicDropdownFilter";

const imageCveFilters = [
  <SearchInput source="q_name_imageRepository_codeRepository_tag" alwaysOn />,
  <DynamicDropdownFilter
    label="Name"
    source="name"
    resource="cve/image-cves"
  />,
  <DynamicDropdownFilter
    label="Image Repository"
    source="imageRepository"
    resource="cve/image-cves"
  />,
  <DynamicDropdownFilter
    label="Code Repository"
    source="codeRepository"
    resource="cve/image-cves"
  />,
  <DynamicDropdownFilter label="Tag" source="tag" resource="cve/image-cves" />,
  <SelectInput source="severity" choices={SEVERITY_CHOICES} />,
];

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <CreateButton />
    <ExportAllButton />
  </TopToolbar>
);

export const ImageCveList = () => (
  <List filters={imageCveFilters} perPage={25} actions={<ListActions />}>
    <Datagrid rowClick="show">
      <TextField source="name" />
      <TextField source="imageRepository" />
      <TextField source="tag" />
      <TextField source="codeRepository" />
      <SelectField source="severity" choices={SEVERITY_CHOICES} />
      <TextField source="vulnerableDependencyName" />
      <TextField source="vulnerableDependencyVersion" />
      <BooleanField source="fixable" />
      <DateField source="createdAt" />
      <DateField source="updatedAt" />
    </Datagrid>
  </List>
);

export const ImageCveShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="name" />
      <TextField source="description" sx={{ whiteSpace: 'pre-wrap' }} />
      <ReferenceField
        link={false}
        label="Image Repository"
        source="imageBuild"
        reference="cve/image-builds"
      >
        <ReferenceField source="imageSource" reference="cve/image-sources">
          <TextField source="imageRepository" />
        </ReferenceField>
      </ReferenceField>
      <TextField source="tag" />
      <TextField source="codeRepository" />
      <NumberField source="cvssScore" />
      <NumberField source="epssScore" />
      <SelectField source="severity" choices={SEVERITY_CHOICES} />
      <TextField source="vulnerableDependencyName" />
      <TextField source="vulnerableDependencyVersion" />
      <StringArrayField source="vulnerableDependencyFixVersions" />
      <BooleanField source="fixable" />
      <DateField source="createdAt" />
      <DateField source="updatedAt" />
    </SimpleShowLayout>
  </Show>
);

export const ImageCveEdit = () => {
  return (
    <Edit redirect="show">
      <SimpleForm>
        <TextInput source="name" disabled />
        <TextInput
          source="description"
          validate={[required()]}
          multiline
          rows={4}
        />
        <TextInput disabled source="imageRepository" />
        <TextInput disabled source="tag" />
        <NumberInput
          source="cvssScore"
          validate={[required(), minValue(0), maxValue(10)]}
          step={0.1}
        />
        <NumberInput
          source="epssScore"
          validate={[required(), minValue(0), maxValue(1)]}
          step={0.00001}
        />
        <SelectInput
          source="severity"
          choices={SEVERITY_CHOICES}
          validate={required()}
        />
        <TextInput source="vulnerableDependencyName" validate={[required()]} />
        <TextInput
          source="vulnerableDependencyVersion"
          validate={[required()]}
        />
        <ArrayInput source="vulnerableDependencyFixVersions">
          <SimpleFormIterator>
            <TextInput source="" />
          </SimpleFormIterator>
        </ArrayInput>
        <BooleanInput source="fixable" />
      </SimpleForm>
    </Edit>
  );
};

export const ImageCveCreate = () => {
  return (
    <Create redirect={"show"}>
      <SimpleForm shouldUnregister>
        <TextInput source="name" validate={[required()]} />
        <TextInput
          source="description"
          validate={[required()]}
          multiline
          rows={4}
        />
        <ReferenceInput source="imageSource" reference="cve/image-sources">
          <AutocompleteInput
            label="Image Source"
            optionText="imageRepository"
          />
        </ReferenceInput>
        <ImageBuildInput />
        <NumberInput
          source="cvssScore"
          validate={[required(), minValue(0), maxValue(10)]}
          step={0.1}
        />
        <NumberInput
          source="epssScore"
          validate={[required(), minValue(0), maxValue(1)]}
          step={0.01}
        />
        <TextInput source="vulnerableDependencyName" validate={[required()]} />
        <TextInput
          source="vulnerableDependencyVersion"
          validate={[required()]}
        />
        <ArrayInput source="vulnerableDependencyFixVersions">
          <SimpleFormIterator>
            <TextInput source="" />
          </SimpleFormIterator>
        </ArrayInput>
      </SimpleForm>
    </Create>
  );
};

const ImageBuildInput = () => {
  const imageSource = useWatch({ name: "imageSource" });
  return imageSource ? (
    <ReferenceInput
      source="imageBuild"
      reference="cve/image-builds"
      filter={{ imageSource }}
    >
      <AutocompleteInput optionText="tag" />
    </ReferenceInput>
  ) : null;
};
